import { useEffect, useState, useRef } from 'react';
import { API_URL } from '../../shared/constant';
import { toast } from 'react-toastify';
import axios from 'axios';
import moment from 'moment';
import Select from 'react-select';
import DatePicker from "react-date-picker";
import LoaderComponent from '../../shared/loader';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';

const PDF_RED = [220, 53, 69];
const PDF_GREEN = [25, 135, 84];
const PDF_GRAY_BG = [247, 247, 248];
const PDF_HEADER_BG = [93, 93, 93];
const PDF_FOOT_BG = [238, 243, 251];

const formatAmount = (value) => {
    const num = Number(value || 0);
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatBalance = (value) => {
    const num = Number(value || 0);
    const isCredit = num < 0;
    return {
        text: `${formatAmount(Math.abs(num))} ${isCredit ? 'Cr' : 'Dr'}`,
        className: isCredit ? 'text-success' : 'text-danger',
    };
};

const pdfColorFor = (balanceClassName) => (balanceClassName === 'text-success' ? PDF_GREEN : PDF_RED);

const pdfParticulars = (row) => {
    const lines = [row.is_opening ? 'Opening Balance' : (row.payment_view || '-')];
    if (row.invoice_no) {
        lines.push(`Invoice #${row.invoice_no}`);
    } else if (row.description) {
        lines.push(row.description);
    }
    if (row.remark) {
        lines.push(row.remark);
    }
    return lines.join('\n');
};

const pdfShadeRow = (rowCells) => rowCells.map((cell) => {
    if (typeof cell === 'string' || typeof cell === 'number') {
        return { content: cell, styles: { fillColor: PDF_GRAY_BG, fontStyle: 'bold' } };
    }
    return { ...cell, styles: { ...(cell.styles || {}), fillColor: PDF_GRAY_BG, fontStyle: 'bold' } };
});

export default function PartyPaymentLedger() {
    const [isLoading, setLoading] = useState(false);
    const [partyId, setPartyId] = useState(0);
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState(null);
    const [partyData, setPartyData] = useState([]);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [partyError, setPartyError] = useState(false);
    const selectRef = useRef();

    useEffect(() => {
        getParty();
    }, []);

    const getParty = () => {
        setLoading(true);
        axios.post(API_URL.PARTY_LIST, { is_seller: [0, 1] }).then(res => {
            let pt = res.data.data.map((row) => {
                return {
                    value: row.id,
                    label: row.name + " - " + row.contact_no
                }
            })
            setPartyData(pt);
            setLoading(false);
        }, error => {
            setLoading(false);
            toast.error("Failed to load parties", {
                autoClose: 3000,
            });
        })
    };

    const fetchLedgerData = (party_id, from_date, to_date) => {
        if (!party_id || party_id === 0) {
            setPartyError(true);
            toast.error("Please select a party", {
                autoClose: 3000,
            });
            return;
        }

        setPartyError(false);
        setLoading(true);
        let params = {
            party_id: party_id,
            from_date: (from_date) ? moment(from_date).format("YYYY-MM-DD") : null,
            to_date: (to_date) ? moment(to_date).format("YYYY-MM-DD") : null,
        }
        axios.post(API_URL.LEDGER_LIST, params).then(res => {
            if (res.data.status === 1) {
                setData(res.data.data || []);
                setSummary(res.data.summary || null);
            } else {
                setData([]);
                setSummary(null);
                toast.error(res.data.message || "No data found", {
                    autoClose: 3000,
                });
            }
            setLoading(false);
        }, error => {
            setLoading(false);
            setData([]);
            setSummary(null);
            toast.error("Something went wrong..", {
                autoClose: 3000,
            });
        })
    };

    const changeParty = (party_id, from_date, to_date) => {
        setPartyId(party_id);
        if (party_id && party_id > 0) {
            fetchLedgerData(party_id, from_date, to_date);
        } else {
            setData([]);
            setSummary(null);
        }
    };

    const searchDataByDate = (fromDate, toDate) => {
        if (partyId && partyId > 0) {
            fetchLedgerData(partyId, fromDate, toDate);
        }
    };

    const clearFilter = () => {
        setStartDate(null);
        setEndDate(null);
        setData([]);
        setSummary(null);
        setPartyId(0);
        setPartyError(false);
        setTimeout(() => {
            selectRef.current.clearValue();
        }, 200);
    };

    const openingBalance = Number((summary && summary.opening_balance) || 0);
    let runningBalance = openingBalance;
    const ledgerRows = data.map((row) => {
        let debitAmt = 0;
        let creditAmt = 0;
        if (!row.is_opening) {
            const amt = Number(row.amount || 0);
            if (row.payment_view === 'Sales') {
                debitAmt = amt;
            } else if (row.payment_view === 'Purchase') {
                creditAmt = amt;
            } else if (row.payment_type === 'Received') {
                creditAmt = amt;
            } else if (row.payment_type === 'Paid') {
                debitAmt = amt;
            }
            runningBalance = runningBalance + debitAmt - creditAmt;
        }
        return { ...row, debitAmt, creditAmt, balance: runningBalance };
    });
    const hasOpeningRow = data.some(row => row.is_opening);
    const totalDebit = ledgerRows.reduce((sum, row) => sum + row.debitAmt, 0);
    const totalCredit = ledgerRows.reduce((sum, row) => sum + row.creditAmt, 0);
    const closingBalance = ledgerRows.length ? ledgerRows[ledgerRows.length - 1].balance : openingBalance;

    const downloadPdf = () => {
        const partyName = (partyData.find(pt => pt.value == partyId) || {}).label || 'Ledger';

        const bodyRows = [];
        if (summary && !hasOpeningRow) {
            bodyRows.push(pdfShadeRow([
                startDate ? moment(startDate).format('DD/MM/YYYY') : '-',
                'Opening Balance',
                '',
                '',
                { content: formatBalance(openingBalance).text, styles: { textColor: pdfColorFor(formatBalance(openingBalance).className), halign: 'right' } },
            ]));
        }
        ledgerRows.forEach((row) => {
            const balance = formatBalance(row.balance);
            const rowCells = [
                row.date_formatted || moment(row.date).format('DD/MM/YYYY'),
                pdfParticulars(row),
                row.debitAmt ? { content: formatAmount(row.debitAmt), styles: { textColor: PDF_RED, halign: 'right' } } : '',
                row.creditAmt ? { content: formatAmount(row.creditAmt), styles: { textColor: PDF_GREEN, halign: 'right' } } : '',
                { content: balance.text, styles: { textColor: pdfColorFor(balance.className), halign: 'right' } },
            ];
            bodyRows.push(row.is_opening ? pdfShadeRow(rowCells) : rowCells);
        });

        const closingBalanceText = formatBalance(closingBalance);
        const footRows = [
            [
                { content: 'Total', colSpan: 2, styles: { halign: 'right' } },
                { content: formatAmount(totalDebit), styles: { halign: 'right', textColor: PDF_RED } },
                { content: formatAmount(totalCredit), styles: { halign: 'right', textColor: PDF_GREEN } },
                '',
            ],
            [
                { content: 'Closing Balance', colSpan: 4, styles: { halign: 'right' } },
                { content: closingBalanceText.text, styles: { halign: 'right', textColor: pdfColorFor(closingBalanceText.className) } },
            ],
        ];

        const doc = new jsPDF({ unit: 'pt', format: 'a4' });
        const marginX = 40;

        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text(partyName, marginX, 40);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        doc.text(
            `${startDate ? moment(startDate).format('DD/MM/YYYY') : 'Beginning'} to ${endDate ? moment(endDate).format('DD/MM/YYYY') : 'Today'}`,
            marginX,
            56
        );

        autoTable(doc, {
            startY: 70,
            head: [['Date', 'Particulars', 'Debit', 'Credit', 'Balance']],
            body: bodyRows,
            foot: footRows,
            showHead: 'everyPage',
            showFoot: 'lastPage',
            theme: 'grid',
            styles: { fontSize: 9, cellPadding: 6, valign: 'middle' },
            headStyles: { fillColor: PDF_HEADER_BG, textColor: 255, fontStyle: 'bold' },
            footStyles: { fillColor: PDF_FOOT_BG, textColor: [33, 37, 41], fontStyle: 'bold' },
            columnStyles: {
                0: { cellWidth: 65 },
                1: { cellWidth: 'auto' },
                2: { cellWidth: 75, halign: 'right' },
                3: { cellWidth: 75, halign: 'right' },
                4: { cellWidth: 85, halign: 'right' },
            },
            margin: { left: marginX, right: marginX, top: 70, bottom: 40 },
        });

        doc.save(`${partyName} - Ledger.pdf`);
    };

    return (
        <div>
            <div className='card'>
                <div className='card-header'>
                    <span className='d-inline-block'>Party Payment Ledger</span>
                </div>
                <div className='card-body'>
                    <div className='row'>
                        <div className='col-md-3 col-sm-12 mb-3'>
                            <div className={`form-group mb-0 ${partyError ? 'error1' : ''}`}>
                                <label>Party <span className="required-text">*</span></label>
                                <Select
                                    ref={selectRef}
                                    placeholder={'Select Party'}
                                    value={partyData.find(pt => pt.value == partyId)}
                                    options={partyData}
                                    onChange={(e) => changeParty((e ? e.value : 0), startDate, endDate)}
                                />
                                {partyError && <span className="text-danger small">Party is required</span>}
                            </div>
                        </div>
                        <div className='col-md-3 col-sm-12 mb-3'>
                            <div className="form-group mb-0">
                                <label>From Date</label>
                                <DatePicker
                                    placeholderText="From Date"
                                    dateFormat="dd/MM/yyyy"
                                    className="form-control input-sm"
                                    selected={startDate}
                                    onChange={(date) => [setStartDate(date), searchDataByDate(date, endDate)]}
                                    value={startDate}
                                />
                            </div>
                        </div>
                        <div className='col-md-3 col-sm-12 mb-3'>
                            <div className="form-group mb-0">
                                <label>To Date</label>
                                <DatePicker
                                    placeholderText="To Date"
                                    dateFormat="dd/MM/yyyy"
                                    className="form-control input-sm"
                                    selected={endDate}
                                    onChange={(date) => [setEndDate(date), searchDataByDate(startDate, date)]}
                                    value={endDate}
                                />
                            </div>
                        </div>
                        <div className='col-md-3 col-sm-12 mb-3 d-flex align-items-end gap-2'>
                            {(partyId || startDate || endDate) && (
                                <button
                                    type='button'
                                    className='btn btn-danger'
                                    onClick={clearFilter}
                                >
                                    <i className="fa fa-times me-1"></i> Clear Filter
                                </button>
                            )}
                            <button
                                type='button'
                                className='btn btn-dark'
                                disabled={!partyId || !data.length}
                                onClick={downloadPdf}
                            >
                                <i className="fa fa-file-pdf-o me-1"></i> Download PDF
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {data.length > 0 && (
                <div className='card mt-3'>
                    <div className='card-header d-flex justify-content-between align-items-center'>
                        <span>{(summary && summary.party_name) || 'Ledger Details'}</span>
                        <span className='ledger-period'>
                            {startDate ? moment(startDate).format('DD/MM/YYYY') : 'Beginning'} to {endDate ? moment(endDate).format('DD/MM/YYYY') : 'Today'}
                        </span>
                    </div>
                    <div className='card-body p-0'>
                        <div className="table-responsive">
                            <table className="table table-hover ledger-table m-0">
                                <thead className="thead">
                                    <tr>
                                        <th>Date</th>
                                        <th>Particulars</th>
                                        <th className='text-end'>Debit</th>
                                        <th className='text-end'>Credit</th>
                                        <th className='text-end'>Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summary && !hasOpeningRow && (
                                        <tr className='ledger-opening-row'>
                                            <td>{startDate ? moment(startDate).format("DD/MM/YYYY") : '-'}</td>
                                            <td>
                                                <div className='ledger-particulars'>
                                                    <span className='ledger-particulars-title'>Opening Balance</span>
                                                </div>
                                            </td>
                                            <td className='text-end'></td>
                                            <td className='text-end'></td>
                                            <td className={`text-end fw-semibold ${formatBalance(openingBalance).className}`}>
                                                {formatBalance(openingBalance).text}
                                            </td>
                                        </tr>
                                    )}
                                    {ledgerRows.map((row, key) => {
                                        const balance = formatBalance(row.balance);
                                        return (
                                            <tr key={key} className={row.is_opening ? 'ledger-opening-row' : ''}>
                                                <td>{row.date_formatted || moment(row.date).format("DD/MM/YYYY")}</td>
                                                <td>
                                                    <div className='ledger-particulars'>
                                                        <span className='ledger-particulars-title'>
                                                            {row.is_opening ? 'Opening Balance' : (row.payment_view || '-')}
                                                        </span>
                                                        {row.invoice_no ? (
                                                            <span className='ledger-particulars-sub'>Invoice #{row.invoice_no}</span>
                                                        ) : (row.description ? (
                                                            <span className='ledger-particulars-sub'>{row.description}</span>
                                                        ) : null)}
                                                        {row.remark && (
                                                            <span className='ledger-particulars-sub text-muted'>{row.remark}</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className='text-end text-danger'>{row.debitAmt ? formatAmount(row.debitAmt) : ''}</td>
                                                <td className='text-end text-success'>{row.creditAmt ? formatAmount(row.creditAmt) : ''}</td>
                                                <td className={`text-end fw-semibold ${balance.className}`}>{balance.text}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr className='ledger-total-row'>
                                        <th colSpan={2} className='text-end'>Total</th>
                                        <th className='text-end text-danger'>{formatAmount(totalDebit)}</th>
                                        <th className='text-end text-success'>{formatAmount(totalCredit)}</th>
                                        <th className='text-end'></th>
                                    </tr>
                                    <tr className='ledger-total-row'>
                                        <th colSpan={4} className='text-end'>Closing Balance</th>
                                        <th className={`text-end ${formatBalance(closingBalance).className}`}>
                                            {formatBalance(closingBalance).text}
                                        </th>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {!isLoading && partyId > 0 && data.length === 0 && (
                <div className='card mt-3'>
                    <div className='card-body text-center text-muted py-4'>
                        No ledger entries found for the selected filters.
                    </div>
                </div>
            )}

            {isLoading && <LoaderComponent mode='full' height={100} width={100}></LoaderComponent>}
        </div>
    )
}
